import { z } from 'zod';
import { ActorSchema } from '../common/actor.schema';
import { AppointmentLinkRecordTypeSchema } from '../communications/calendar.schemas';

/**
 * Normalised mailbox + calendar events emitted by the Nylas webhook parser
 * (IMM-88 / ADR 0018). Vendor-neutral by design: the parser fills these from the
 * data present in the Nylas notification only — the Nylas `grantId` and resource
 * ids plus whatever metadata the notification carries. Cross-aggregate resolution
 * (`grantId → workspaceId, memberId`) is the consuming module's job, not the
 * parser's, so no tenant identifiers appear here.
 *
 * Every payload carries `providerEventId` — the upstream provider's notification
 * id, stable across redelivery — so consumers dedupe on it (PRD § idempotency on
 * the vendor event id) rather than the webhook row or resource id.
 */

/** Inbound message landed in a member's mailbox (`message.created`). */
export const MessageReceivedPayloadSchema = z.object({
  providerEventId: z.string().min(1),
  grantId: z.string().min(1),
  messageId: z.string().min(1),
  threadId: z.string().min(1).optional(),
  subject: z.string().optional(),
  from: z.string().optional(),
  // The sender's display name, when the provider reported one. Carried alongside
  // `from` (never folded into it): the address is the identity the contact
  // auto-match keys on, the name is only ever presentation.
  fromName: z.string().optional(),
  to: z.array(z.string()).optional(),
  // Recipient display names, positionally aligned with `to` (null where the
  // provider gave no name).
  toNames: z.array(z.string().nullable()).optional(),
  snippet: z.string().optional(),
  receivedAt: z.iso.datetime().optional(),
});

/** A member-initiated send left the provider (`message.send_success`). */
export const MessageSentPayloadSchema = z.object({
  providerEventId: z.string().min(1),
  grantId: z.string().min(1),
  messageId: z.string().min(1),
  actor: z.literal('member'),
  sentAt: z.iso.datetime().optional(),
});

/** A reference locating the message a failed send was attempting. */
export const SentMessageRefSchema = z.object({
  grantId: z.string().min(1),
  messageId: z.string().min(1),
});

/** A member-initiated send failed at the provider (`message.send_failed`). */
export const MessageSendFailedPayloadSchema = z.object({
  providerEventId: z.string().min(1),
  grantId: z.string().min(1),
  sentMessageRef: SentMessageRefSchema,
  reason: z.string().optional(),
});

/** Calendar event created/updated/deleted at the source (`event.*`). */
export const CalendarEventChangedPayloadSchema = z.object({
  providerEventId: z.string().min(1),
  grantId: z.string().min(1),
  eventId: z.string().min(1),
  calendarId: z.string().min(1).optional(),
  occurredAt: z.iso.datetime().optional(),
});

/**
 * Something changed in a member's mailbox at the provider (Gmail push, IMM-530.5).
 * Deliberately the thinnest event in this file: Gmail's Pub/Sub notification carries
 * only the address and a history cursor, and working out WHICH messages changed needs
 * the grant's sealed credential plus its stored watermark — both `communications`-owned.
 * So the parser emits the notification verbatim and the consuming module does the diff
 * (ADR 0016 forbids a parser reaching into another module's schema or tenant credentials).
 *
 * `emailAddress` maps to zero, one, or SEVERAL grants: the address is not unique on
 * `mailbox_grants`, since two members can connect the same shared inbox.
 */
export const MailboxChangedPayloadSchema = z.object({
  // The Pub/Sub `messageId` — stable across redelivery, which is what makes it the
  // dedupe key rather than the history cursor (Gmail re-pushes the same cursor).
  providerEventId: z.string().min(1),
  emailAddress: z.string().min(1),
  // Gmail serialises this as a number in some responses and a string in others; the
  // parser normalises to string so the watermark comparison never depends on which.
  historyId: z.string().min(1),
});

/**
 * A Google Calendar push channel fired (IMM-530.5) — the calendar twin of
 * {@link MailboxChangedPayloadSchema}, and just as thin. A channel notification
 * carries no event data at all, only headers naming the channel, so the consuming
 * module resolves the channel to a grant and fetches the delta with its `syncToken`.
 */
export const CalendarChangedPayloadSchema = z.object({
  // `${channelId}:${X-Goog-Message-Number}` — the message number is monotonic per
  // channel, so the pair is unique and stable across Google's redeliveries.
  providerEventId: z.string().min(1),
  channelId: z.string().min(1),
  // Google's own handle for the watched resource — required to stop the channel.
  resourceId: z.string().min(1),
});

/**
 * A change notification from a provider that names the changed resource itself
 * (Microsoft Graph, IMM-1170.3). Vendor-neutral and as thin as the notification:
 * the consuming module resolves the subscription to a grant and reads the one
 * resource with that grant's credential.
 */
export const SubscriptionResourceChangedPayloadSchema = z.object({
  // `${subscriptionId}:${resourceId}:${changeType}:${etag}` — stable across redeliveries.
  providerEventId: z.string().min(1),
  subscriptionId: z.string().min(1),
  resourceKind: z.enum(['MESSAGE', 'EVENT']),
  resourceId: z.string().min(1),
  changeType: z.enum(['created', 'updated', 'deleted']),
});

/** A member's OAuth grant lapsed (`grant.expired`) — drives the reconnect CTA. */
export const MailboxGrantExpiredPayloadSchema = z.object({
  providerEventId: z.string().min(1),
  grantId: z.string().min(1),
});

/** A member revoked their OAuth grant (`grant.deleted`) — local cleanup. */
export const MailboxGrantRevokedPayloadSchema = z.object({
  providerEventId: z.string().min(1),
  grantId: z.string().min(1),
});

/**
 * An email thread was materialised in the read-model AND matched to exactly one
 * workspace contact (IMM-233.1.1). Enriched — unlike the raw vendor `message.*`
 * events, this one carries the resolved tenant + contact identifiers — so the
 * `activity` module can project a `contact.email-thread` timeline entry without
 * touching the mailbox grant or the contact-email lookup itself (module
 * decoupling). Emitted only on a match; an unassigned thread produces no event.
 */
export const EmailThreadUpdatedPayloadSchema = z.object({
  workspaceId: z.string().min(1),
  // The grant-owning member. Nylas thread ids are per-grant, NOT globally
  // unique, so the activity projection keys on (workspaceId, memberId,
  // providerThreadId) — the same read-model scope — to avoid a cross-tenant
  // collision on the global (content_type, content_id) unique index.
  memberId: z.string().min(1),
  // Carried from the matched contact; null when the contact has no Standort org
  // (kept nullable so the activity projection stays workspace-visible).
  organizationId: z.string().min(1).nullable(),
  contactId: z.string().min(1),
  providerThreadId: z.string().min(1),
  // Nullable rather than optional so the payload is a flat record with no
  // `undefined` values (it rides the outbox as a DomainEvent payload).
  subject: z.string().nullable(),
  // Preview of the newest message's body, denormalised with subject/direction from
  // the recomputed thread — drives the contact-timeline card snippet (design 01).
  snippet: z.string().nullable(),
  // Primary To of the newest message (`toEmails[0]`), denormalised alongside
  // subject/snippet/direction — drives the timeline card's `An:` line (design 01).
  // Null when the newest message carries no recipient. Forward-only: threads with
  // no new message since the field shipped project it as null (no backfill).
  recipient: z.string().nullable(),
  direction: z.enum(['INBOUND', 'OUTBOUND']),
  occurredAt: z.iso.datetime(),
});

/**
 * An email thread was DETACHED from the contact it was assigned to (IMM-233.4) —
 * a member removed the assignment, or "assign all from this sender" re-homed the
 * thread off a previous contact. The mirror of `email-thread-updated`: the
 * `activity` module consumes it to RETRACT the projected `contact.email-thread`
 * timeline entry (delete-by-content on the same deterministic key the record path
 * used), so the email disappears from that contact's timeline. Carries the
 * `contactId` the thread is being detached FROM. The email/thread itself is never
 * deleted — only the contact-timeline projection is retracted.
 */
export const EmailThreadUnassignedPayloadSchema = z.object({
  workspaceId: z.string().min(1),
  // The grant-owning member — half of the (workspaceId, memberId, providerThreadId)
  // scope the activity projection keys the retract on (Nylas thread ids are per-grant).
  memberId: z.string().min(1),
  providerThreadId: z.string().min(1),
  // The contact the thread is detached FROM — the CONTACT link the retract removes.
  contactId: z.string().min(1),
});

/**
 * A personal thread with a placement changed (a message synced, the placement
 * assigned, moved or removed). Deliberately thin: the activity module reconciles
 * the thread's e-mail activities from the read model's current truth, never from
 * this payload (IMM-866.1). Company-mailbox threads never publish it.
 */
export const EmailThreadChangedPayloadSchema = z.object({
  workspaceId: z.string().min(1),
  // email_threads.member_id — a member id for a personal mailbox.
  ownerId: z.string().min(1),
  providerThreadId: z.string().min(1),
});

/**
 * A calendar appointment is linked to a record, or a linked appointment changed
 * (IMM-234.8). The `activity` module records / amends the `calendar.appointment`
 * timeline entry for that one (appointment, record) pair.
 */
export const AppointmentLinkedPayloadSchema = z.object({
  workspaceId: z.string().min(1),
  appointmentId: z.string().min(1),
  recordType: AppointmentLinkRecordTypeSchema,
  recordId: z.string().min(1),
  // The member whose calendar holds the appointment: for one created in immoOS, the member who created it. The
  // timeline names them as the entry's author (IMM-1490). Optional only for events already in the outbox at deploy.
  memberId: z.string().min(1).optional(),
  title: z.string(),
  appointmentType: z.string(),
  startsAt: z.iso.datetime(),
  status: z.enum(['CONFIRMED', 'CANCELLED']),
  occurredAt: z.iso.datetime(),
});

/** The mirror of {@link AppointmentLinkedPayloadSchema}: the entry on that record is retracted. */
export const AppointmentUnlinkedPayloadSchema = z.object({
  workspaceId: z.string().min(1),
  appointmentId: z.string().min(1),
  recordType: AppointmentLinkRecordTypeSchema,
  recordId: z.string().min(1),
});

export type MessageReceivedPayload = z.infer<typeof MessageReceivedPayloadSchema>;
export type MessageSentPayload = z.infer<typeof MessageSentPayloadSchema>;
export type SentMessageRef = z.infer<typeof SentMessageRefSchema>;
export type MessageSendFailedPayload = z.infer<typeof MessageSendFailedPayloadSchema>;
export type CalendarEventChangedPayload = z.infer<typeof CalendarEventChangedPayloadSchema>;
export type MailboxChangedPayload = z.infer<typeof MailboxChangedPayloadSchema>;
export type CalendarChangedPayload = z.infer<typeof CalendarChangedPayloadSchema>;
export type SubscriptionResourceChangedPayload = z.infer<typeof SubscriptionResourceChangedPayloadSchema>;
export type MailboxGrantExpiredPayload = z.infer<typeof MailboxGrantExpiredPayloadSchema>;
export type MailboxGrantRevokedPayload = z.infer<typeof MailboxGrantRevokedPayloadSchema>;
export type EmailThreadUpdatedPayload = z.infer<typeof EmailThreadUpdatedPayloadSchema>;
export type EmailThreadChangedPayload = z.infer<typeof EmailThreadChangedPayloadSchema>;
export type EmailThreadUnassignedPayload = z.infer<typeof EmailThreadUnassignedPayloadSchema>;
export type AppointmentLinkedPayload = z.infer<typeof AppointmentLinkedPayloadSchema>;
export type AppointmentUnlinkedPayload = z.infer<typeof AppointmentUnlinkedPayloadSchema>;

/**
 * A member placed one of their own Konversationen on a record, or removed its
 * placement (IMM-1095). The write's audit record; `email-thread-changed` is the
 * reconcile trigger beside it. `subjectType` and `subjectId` are null when the
 * placement was removed.
 */
export const EmailThreadPlacementChangedPayloadSchema = z.object({
  workspaceId: z.uuid(),
  providerThreadId: z.string().min(1),
  subjectType: z.string().nullable(),
  subjectId: z.string().nullable(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type EmailThreadPlacementChangedPayload = z.infer<typeof EmailThreadPlacementChangedPayloadSchema>;

/**
 * A member created, edited or cancelled an appointment in immoOS (IMM-1095).
 * The write's audit record; the link projections beside it are re-projections,
 * and a change pulled from the provider publishes none of these.
 */
export const AppointmentWrittenPayloadSchema = z.object({
  workspaceId: z.uuid(),
  appointmentId: z.string().min(1),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type AppointmentWrittenPayload = z.infer<typeof AppointmentWrittenPayloadSchema>;
